<template>
  <v-chip
    v-if="getAccountState"
    :color="statusMap[getAccountState].color"
    variant="tonal"
    size="small"
  >
    {{ statusMap[getAccountState].label }}
  </v-chip>
</template>

<script lang="ts" setup>
import { StripeAccountStatus } from "@/shared/enums/status.enum";
import Stripe from "stripe";
import { useAccountStore } from "@/account/stores/account";
import { computed } from "vue";
import i18n from "@/app/plugins/i18n";

const getAccountState = computed(() => {
  const account = useAccountStore().account;
  let state;
  if (account) {
    // Order is important
    // The platform can reject an account at any time, so check for this state first
    if (isRejected(account)) {
      state = StripeAccountStatus.REJECTED;
    } else if (isRestricted(account)) {
      state = StripeAccountStatus.RESTRICTED;
    } else if (isRestrictedSoon(account)) {
      state = StripeAccountStatus.RESTRICTED_SOON;
    } else if (isPendingEnabled(account)) {
      state = StripeAccountStatus.PENDING_ENABLED;
    } else if (isPendingDisabled(account)) {
      state = StripeAccountStatus.PENDING_DISABLED;
    } else if (isEnabled(account)) {
      state = StripeAccountStatus.ENABLED;
    } else if (isComplete(account)) {
      state = StripeAccountStatus.COMPLETE;
    } else {
      state = StripeAccountStatus.UNKNOWN;
    }
  }
  return state;
});

const statusMap = {
  [StripeAccountStatus.UNKNOWN]: {
    label: i18n.global.t(`page.account.statuses.${StripeAccountStatus.UNKNOWN}`),
    color: "grey-lighten-2",
  },
  [StripeAccountStatus.RESTRICTED]: {
    label: i18n.global.t(`page.account.statuses.${StripeAccountStatus.RESTRICTED}`),
    color: "red-lighten-1",
  },
  [StripeAccountStatus.COMPLETE]: {
    label: i18n.global.t(`page.account.statuses.${StripeAccountStatus.COMPLETE}`),
    color: "green-lighten-1",
  },
  [StripeAccountStatus.ENABLED]: {
    label: i18n.global.t(`page.account.statuses.${StripeAccountStatus.ENABLED}`),
    color: "blue-darken-2",
  },
  [StripeAccountStatus.RESTRICTED_SOON]: {
    label: i18n.global.t(`page.account.statuses.${StripeAccountStatus.RESTRICTED_SOON}`),
    color: "yellow-darken-2",
  },
  [StripeAccountStatus.REJECTED]: {
    label: i18n.global.t(`page.account.statuses.${StripeAccountStatus.REJECTED}`),
    color: "red-lighten-1",
  },
  [StripeAccountStatus.PENDING_ENABLED]: {
    label: i18n.global.t(`page.account.statuses.${StripeAccountStatus.PENDING_ENABLED}`),
    color: "grey-lighten-2",
  },
  [StripeAccountStatus.PENDING_DISABLED]: {
    label: i18n.global.t(`page.account.statuses.${StripeAccountStatus.PENDING_DISABLED}`),
    color: "grey-lighten-2",
  },
};

const isRejectedReason = (reason: string) => {
  return reason?.startsWith("rejected");
};

const isRestricted = (account: Stripe.Account) => {
  return account.payouts_enabled === false || account.capabilities?.card_payments === "inactive";
};

const isRestrictedSoon = (account: Stripe.Account) => {
  return account.requirements?.current_deadline !== null;
};

const isRejected = (account: Stripe.Account) => {
  return isRejectedReason(account.requirements?.disabled_reason || "");
};

const isComplete = (account: Stripe.Account) => {
  return (account.requirements?.eventually_due || []).length === 0;
};

const isEnabled = (account: Stripe.Account) => {
  return (
    account.payouts_enabled === true &&
    (!account.capabilities?.card_payments || account.capabilities?.card_payments === "active") &&
    (account.requirements?.eventually_due || []).length > 0 &&
    account.requirements?.current_deadline === null
  );
};

const isPendingEnabled = (account: Stripe.Account) => {
  return (
    (account.requirements?.pending_verification || []).length > 0 &&
    account.requirements?.disabled_reason === null
  );
};

const isPendingDisabled = (account: Stripe.Account) => {
  return (
    (account.requirements?.pending_verification || []).length > 0 &&
    account.requirements?.disabled_reason !== null
  );
};
</script>
